import {
  QAppsCountCard,
  QAppVersionsChartCard,
  QNewlySubmittedAppsCard,
  QAppTypeDistributionCard,
  QTopAppVersionsCard,
  QTopAppCategoriesCard,
} from '@kw/quokka-ui';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import moment from 'moment';
import _ from 'lodash';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics, rollingSubmittedDateRange } from './dashboardAnalytics';

type AppInventoryProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

export const AppInventory = ({ dateRangeDays, metrics }: AppInventoryProps) => {
  const navigate = useNavigate();

  const navigateToApplications = ({
    platform = 'all',
    appType = 'all',
    analysisStatus = 'all',
    categories = 'all',
    searchQuery = '',
  }: {
    platform?: 'all' | 'android' | 'ios';
    appType?: string;
    analysisStatus?: string;
    categories?: string;
    searchQuery?: string;
  }) => {
    const { startDate, endDate } = rollingSubmittedDateRange(dateRangeDays);
    navigate({
      pathname: '/applications',
      search: createSearchParams({
        platform,
        appType,
        startDate,
        endDate,
        analysisStatus,
        categories,
        searchQuery,
      }).toString(),
    });
  };

  const appsOverTime = _.get(metrics, 'appsOverTime', []).map(entry => ({
    ...entry,
    date: moment(entry.date).format('MMM D'),
  }));

  const newlySubmittedApps = _.get(metrics, 'newlySubmittedApps', []).map(entry => ({
    ...entry,
    onClick: () =>
      navigate({
        pathname: '/applications/app-detail-report',
        search: createSearchParams({
          analysisId: entry.analysisId,
          backQParams: '',
        }).toString(),
      }),
  }));

  const appTypeDistribution = Object.fromEntries(
    _.get(metrics, 'appTypeDistribution', []).map(item => [item.managementType, item.versionCount]),
  );

  const platforms = ['android', 'ios'] as const;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >
      {/* Row 1: Summary cards — each takes 1/3 (2 of 6 cols) */}
      <Box sx={{ gridColumn: 'span 2' }}>
        <QAppsCountCard
          data-testid="total-versions-card"
          variant="total"
          versionsCount={metrics?.totalVersionsAll}
          uniqueAppsCount={metrics?.uniqueAppsAll}
          onClick={() => navigateToApplications({ platform: 'all' })}
        />
      </Box>
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 2' }}>
          <QAppsCountCard
            data-testid={`${platform}-versions-card`}
            variant={platform}
            versionsCount={platform === 'android' ? metrics?.totalVersionsAndroid : metrics?.totalVersionsIos}
            uniqueAppsCount={platform === 'android' ? metrics?.uniqueAppsAndroid : metrics?.uniqueAppsIos}
            onClick={() => navigateToApplications({ platform })}
          />
        </Box>
      ))}
      {/* Row 2-3: Chart (2/3) + Newly Submitted (1/3, spans 2 rows), then App Type Distribution (2/3) under chart */}
      <Box sx={{ gridColumn: 'span 4' }}>
        <QAppVersionsChartCard data={appsOverTime} />
      </Box>
      <Box sx={{ gridColumn: 'span 2', gridRow: 'span 2' }}>
        <QNewlySubmittedAppsCard entries={newlySubmittedApps} />
      </Box>
      <Box sx={{ gridColumn: 'span 4' }}>
        <QAppTypeDistributionCard
          managed={_.get(appTypeDistribution, 'MANAGED', 0)}
          requested={_.get(appTypeDistribution, 'REQUESTED', 0)}
          unmanaged={_.get(appTypeDistribution, 'UNMANAGED', 0)}
          onCountClick={item =>
            navigateToApplications({
              appType: item === 'Unmanaged' ? 'DISCOVERED' : _.upperCase(item),
            })
          }
        />
      </Box>

      {/* Row 4: Top App Versions Android + iOS side-by-side (1/2 each) */}
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 3' }}>
          <QTopAppVersionsCard
            platform={platform}
            data={platform === 'android' ? _.get(metrics, 'topAppVersionsAndroid', []) : _.get(metrics, 'topAppVersionsIos', [])}
            onVersionCountClick={item => navigateToApplications({ searchQuery: item.applicationId, platform })}
          />
        </Box>
      ))}

      {/* Row 5: Top App Categories Android + iOS side-by-side (1/2 each) */}
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 3' }}>
          <QTopAppCategoriesCard
            platform={platform}
            data={platform === 'android' ? _.get(metrics, 'topAppCategoriesAndroid', []) : _.get(metrics, 'topAppCategoriesIos', [])}
            onVersionCountClick={item => navigateToApplications({ categories: item.category, platform })}
          />
        </Box>
      ))}
    </Box>
  );
};
